import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createVaccinationRecord,
  fetchVaccinationDashboard,
  fetchVaccinationLookup,
  fetchVaccinationReport,
  fetchVaccinationRows,
  patchVaccinationRecord,
  type VaccinationLookupType,
} from './vaccination.api';

export const useVaccinationDashboard = (enabled: boolean) => useQuery({
  queryKey: ['vaccination', 'dashboard'],
  queryFn: fetchVaccinationDashboard,
  enabled,
});

export const useVaccinationRows = (resource: string, filters: Record<string, unknown>, enabled: boolean) => useQuery({
  queryKey: ['vaccination', resource, filters],
  queryFn: () => fetchVaccinationRows(resource, filters),
  enabled,
});

export const useVaccinationLookup = (
  type: VaccinationLookupType,
  params: Record<string, unknown> = {},
  enabled = true,
) => useQuery({
  queryKey: ['vaccination', 'lookup', type, params],
  queryFn: () => fetchVaccinationLookup(type, params),
  enabled,
  staleTime: type === 'patients' ? 0 : 60_000,
});

export const useVaccinationReport = (params: Record<string, unknown>) => useQuery({
  queryKey: ['vaccination', 'report', params],
  queryFn: () => fetchVaccinationReport(params),
});

export const useCreateVaccinationRecord = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ resource, payload }: { resource: string; payload: object }) => createVaccinationRecord(resource, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['vaccination'] }),
  });
};

export const usePatchVaccinationRecord = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ resource, id, payload, action }: { resource: string; id: string; payload: object; action?: string }) => patchVaccinationRecord(resource, id, payload, action),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['vaccination'] }),
  });
};
